/**
 * Image compression utility to reduce image file sizes before upload
 * Uses canvas-based compression with progressive quality reduction
 */

/**
 * Compresses an image file to meet a target size limit in bytes
 * @param {File} file - The original image file to compress
 * @param {number} targetSizeInBytes - Target size in bytes (default: 3MB)
 * @param {number} initialQuality - Initial compression quality (0-1, default: 0.9)
 * @param {number} minQuality - Minimum quality to try before giving up (0-1, default: 0.3)
 * @param {number} maxWidth - Maximum width to resize to if needed (default: 3000px)
 * @param {number} maxHeight - Maximum height to resize to if needed (default: 3000px)
 * @returns {Promise<File|null>} - Compressed file or null if compression failed
 */
export async function compressImage(
    file: File,
    targetSizeInBytes: number = 3 * 1024 * 1024, // 3MB default
    initialQuality: number = 0.9,
    minQuality: number = 0.3,
    maxWidth: number = 3000,
    maxHeight: number = 3000
): Promise<File | null> {
    // Only process image files
    if (!file.type.startsWith('image/')) {
        console.log('Not an image file, skipping compression');
        return file;
    }

    // Skip GIFs (they can't be properly compressed this way)
    if (file.type === 'image/gif') {
        console.log('GIF file, skipping compression');
        return file;
    }

    // Skip small files that are already under the limit
    if (file.size <= targetSizeInBytes) {
        console.log('File already under target size, skipping compression');
        return file;
    }

    // Create a logging prefix for tracking this specific compression
    const logPrefix = `[Compress ${file.name.substring(0, 15)}${file.name.length > 15 ? '...' : ''}]`;
    console.log(`${logPrefix} Original size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);

    try {
        // Create an image object
        const img = new Image();
        const imgLoaded = new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = (e) => reject(new Error('Failed to load image for compression'));
        });

        // Load the image
        img.src = URL.createObjectURL(file);
        await imgLoaded;

        // Determine if we need to resize based on dimensions
        let width = img.width;
        let height = img.height;
        let scaleFactor = 1;

        // Calculate scale factor if image is too large
        if (width > maxWidth || height > maxHeight) {
            scaleFactor = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * scaleFactor);
            height = Math.round(height * scaleFactor);
            console.log(`${logPrefix} Resizing from ${img.width}x${img.height} to ${width}x${height}`);
        }

        // Create canvas for compression
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Could not get 2D context from canvas');
        }

        // Draw image to canvas at new dimensions
        ctx.drawImage(img, 0, 0, width, height);

        // Release the original object URL
        URL.revokeObjectURL(img.src);

        // Try different quality settings until we get under the target size
        let quality = initialQuality;
        let blob: Blob | null = null;
        let currentSize = Infinity;
        let compressionAttempt = 1;
        const targetType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

        while (currentSize > targetSizeInBytes && quality >= minQuality) {
            // Convert canvas to blob with current quality
            blob = await new Promise<Blob | null>(resolve => {
                canvas.toBlob(
                    resultBlob => resolve(resultBlob),
                    targetType,
                    quality
                );
            });

            if (!blob) {
                throw new Error('Canvas toBlob() failed');
            }

            currentSize = blob.size;
            console.log(
                `${logPrefix} Attempt ${compressionAttempt++}: ` +
                `Quality ${(quality * 100).toFixed(0)}% = ${(currentSize / (1024 * 1024)).toFixed(2)}MB`
            );

            // If still too big, reduce quality and try again
            if (currentSize > targetSizeInBytes) {
                // Adaptive quality reduction - reduce more aggressively for larger files
                const sizeRatio = currentSize / targetSizeInBytes;
                if (sizeRatio > 5) {
                    quality -= 0.2; // Reduce by 0.2 for very large files
                } else if (sizeRatio > 2) {
                    quality -= 0.1; // Reduce by 0.1 for moderately large files
                } else {
                    quality -= 0.05; // Small reductions for files close to target
                }
                // Ensure quality doesn't go below minimum
                quality = Math.max(quality, minQuality);
            }
        }

        // Clean up memory by removing canvas
        canvas.width = 0;
        canvas.height = 0;

        // Check if compression was successful
        if (currentSize <= targetSizeInBytes) {
            // Create a new File object from the blob
            const compressedFile = new File(
                [blob!],
                file.name,
                {
                    type: targetType,
                    lastModified: file.lastModified
                }
            );

            console.log(
                `${logPrefix} Compression successful: ` +
                `${(file.size / (1024 * 1024)).toFixed(2)}MB → ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB ` +
                `(${Math.round((1 - compressedFile.size / file.size) * 100)}% reduction)`
            );

            return compressedFile;
        } else {
            // If we couldn't compress to the target size, return the best we could do
            console.warn(
                `${logPrefix} Could not compress to target size. ` +
                `Best result: ${(currentSize / (1024 * 1024)).toFixed(2)}MB at quality ${(quality * 100).toFixed(0)}%`
            );

            if (blob && blob.size < file.size) {
                // At least return the improved version
                const bestAttemptFile = new File(
                    [blob],
                    file.name,
                    {
                        type: targetType,
                        lastModified: file.lastModified
                    }
                );

                console.log(
                    `${logPrefix} Returning best attempt: ` +
                    `${(bestAttemptFile.size / (1024 * 1024)).toFixed(2)}MB ` +
                    `(${Math.round((1 - bestAttemptFile.size / file.size) * 100)}% reduction)`
                );

                return bestAttemptFile;
            }

            return file; // Return original if we somehow made it larger
        }
    } catch (error) {
        console.error(`${logPrefix} Compression failed:`, error);
        return file; // Return original file on error
    }
}

/**
 * Processes a file for upload, compressing if it's an image above the size limit
 * @param {File} file - The file to process
 * @param {number} sizeLimit - Size limit in bytes
 * @returns {Promise<File>} - Processed file (compressed if needed)
 */
export async function processFileForUpload(
    file: File,
    sizeLimit: number = 3 * 1024 * 1024 // 3MB default
): Promise<File> {
    // Only compress images, pass through other file types
    if (!file.type.startsWith('image/') || file.type === 'image/gif') {
        return file;
    }

    // If file is under the limit, no compression needed
    if (file.size <= sizeLimit) {
        return file;
    }

    // Compress the image
    const compressedFile = await compressImage(file, sizeLimit);

    // Return the compressed file or the original if compression failed
    return compressedFile || file;
} 